import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Hammer, ExternalLink, ArrowRight, Package, ChefHat, Truck, Wrench, Check, Star, Clock, Shield, Phone, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Images - Montage et assemblage modernes
const HERO_IMAGE = "https://images.pexels.com/photos/4554421/pexels-photo-4554421.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CONTENT_IMAGE_1 = "https://images.pexels.com/photos/245208/pexels-photo-245208.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CONTENT_IMAGE_2 = "https://images.unsplash.com/photo-1758448755952-42b404bc6f39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBwcm9wZXJ0eSUyMG1hbmFnZW1lbnQlMjBzZXJ2aWNlcyUyMGx1eHVyeXxlbnwwfHx8fDE3NzQxMjkwNDl8MA&ixlib=rb-4.1.0&q=85";

export const MontagePage = () => {
  const navigate = useNavigate();

  const services = [
    { 
      icon: Package,
      title: 'Montage de meubles', 
      description: 'IKEA, Conforama, Lipo, Pfister et autres',
      link: '/montage/meubles' 
    },
    { 
      icon: ChefHat,
      title: 'Agencement de cuisine', 
      description: 'Installation complète de cuisines équipées',
      link: '/montage/cuisine' 
    },
    { 
      icon: Truck,
      title: 'Transport & Livraison', 
      description: 'Livraison meubles à domicile',
      link: '/montage/transport' 
    },
    { 
      icon: Wrench,
      title: 'Handy Man', 
      description: 'Petites réparations et entretien',
      link: '/montage/handyman' 
    }
  ];

  const questions = [
    { id: 'service', label: 'Quel service vous intéresse ?', type: 'select', options: ['Montage de meubles', 'Agencement cuisine', 'Transport & Livraison', 'Petite réparation', 'Entretien général', 'Autre'] },
    { id: 'type_bien', label: 'Quel type de bien ?', type: 'select', options: ['Appartement', 'Maison', 'Bureau', 'Local commercial'] },
    { id: 'localisation', label: 'Localisation ?', type: 'text', placeholder: 'Ex: Lausanne, Genève...' },
    { id: 'volume', label: 'Volume de travail estimé ?', type: 'select', options: ['Petit (1-2h)', 'Moyen (demi-journée)', 'Grand (journée)', 'Sur devis'] },
    { id: 'urgence', label: 'Délai souhaité ?', type: 'select', options: ['Urgent (sous 48h)', 'Cette semaine', 'Semaine prochaine', 'Flexible'] }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="montage-page">
      <Header />
      
      {/* ============================================
          PILIER 1: ATTENTION - Hero avec personne assemblant
          ============================================ */}
      <section className="hero-section relative min-h-[70vh] sm:min-h-[80vh] flex items-center">
        <img 
          src={HERO_IMAGE} 
          alt="Personne assemblant un meuble avec précision" 
          className="hero-image-bg"
        />
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-10 sm:py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <Hammer className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 text-white" />
              <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm text-xs sm:text-sm">
                Homely Montage
              </Badge>
            </div>
            
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 text-white leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Montage, installation et petits travaux sans stress
            </h1>
            
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-4 sm:mb-6 max-w-2xl">
              Montage de meubles IKEA, Conforama ou Pfister, agencement de cuisine, transport et livraison, 
              petites réparations : nos équipes Handy Man interviennent rapidement et proprement.
            </p>

            {/* Métriques clés */}
            <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4 mb-5 sm:mb-8">
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                <span className="text-white text-xs sm:text-sm font-semibold">Sous 48h</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
                <span className="text-white text-xs sm:text-sm">4.9/5 satisfaction</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
                <span className="text-white text-xs sm:text-sm">Prix fixe</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                size="lg" 
                className="btn-premium h-12 sm:h-14 px-6 sm:px-10 text-sm sm:text-base rounded-full w-full sm:w-auto"
                onClick={() => document.getElementById('form').scrollIntoView({ behavior: 'smooth' })}
                data-testid="montage-cta-primary"
              >
                Demander un devis gratuit
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base rounded-full bg-white/10 border-white/30 text-white hover:bg-white hover:text-[var(--anthracite)] backdrop-blur-sm gap-2 w-full sm:w-auto"
                onClick={() => window.open('https://www.homely-montage.ch', '_blank')}
              >
                <ExternalLink className="h-4 w-4" />
                <span className="hidden xs:inline">homely-montage.ch</span>
                <span className="xs:hidden">Site web</span>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 2: ÉMOTION - Les galères du montage
          ============================================ */}
      <section className="py-12 sm:py-16 md:py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            <div>
              <Badge className="mb-3 sm:mb-4 bg-orange-100 text-orange-700 text-xs sm:text-sm">Le constat</Badge>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Le montage de meubles : une source de stress évitable
              </h2>
              
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground mb-4 sm:mb-6">
                Vous venez de recevoir votre commande IKEA ou Conforama. 15 cartons, des centaines de vis, 
                une notice incompréhensible et des heures de galère en perspective. Sans parler du mal de dos 
                et des disputes familiales qui en découlent...
              </p>

              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {[
                  { icon: '📦', title: 'Cartons qui s\'accumulent', desc: 'Votre salon ressemble à un entrepôt depuis des semaines' },
                  { icon: '🔧', title: 'Outils inadaptés', desc: 'Une vis foirée et c\'est le meuble entier qui vacille' },
                  { icon: '⏰', title: 'Week-end sacrifié', desc: '6h pour monter une armoire, vraiment ?' },
                  { icon: '🤕', title: 'Mal de dos garanti', desc: 'Porter des cartons lourds sans technique = blessure' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 bg-secondary/30 rounded-lg">
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-lg font-semibold" style={{ color: 'var(--sage-dark)' }}>
                Et si vous pouviez profiter de vos meubles sans y passer votre temps ?
              </p>
            </div>

            <div className="relative">
              <img 
                src={CONTENT_IMAGE_1} 
                alt="Intérieur moderne avec mobilier design assemblé" 
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg max-w-xs">
                <p className="font-bold text-3xl" style={{ color: 'var(--sage)' }}>2h</p>
                <p className="text-sm text-muted-foreground">pour monter ce qu'un particulier met 6h à faire</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 3: MÉMOIRE - Nos services détaillés
          ============================================ */}
      <section className="py-20 gradient-premium">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>Nos services</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Montage, transport et petits travaux
            </h2>
            <div className="section-divider"></div>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Nos Handy Man professionnels interviennent pour tous vos besoins d'assemblage, 
              d'installation et de petite maintenance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {services.map((service, idx) => (
              <Card 
                key={idx} 
                className="card-premium cursor-pointer group text-center"
                onClick={() => navigate(service.link)}
                data-testid={`service-card-${idx}`}
              >
                <CardHeader>
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mx-auto mb-4 transition-colors" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>
                    <service.icon className="h-7 w-7" />
                  </div>
                  <CardTitle className="text-lg">{service.title}</CardTitle>
                  <CardDescription>{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full group-hover:bg-[var(--sage)] group-hover:text-white group-hover:border-[var(--sage)]">
                    En savoir plus
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Image + Liste avantages */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <img 
              src={CONTENT_IMAGE_2} 
              alt="Salon moderne avec mobilier parfaitement installé" 
              className="rounded-2xl shadow-xl w-full"
            />
            <div>
              <h3 className="text-2xl font-bold mb-6">Ce qui nous différencie :</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  'Intervention sous 48h',
                  'Techniciens expérimentés',
                  'Outillage professionnel',
                  'Prix fixe annoncé',
                  'Chantier propre',
                  'Évacuation déchets incluse',
                  'Garantie montage',
                  'Assurance RC',
                  'Facturation claire',
                  'Service ponctuel et fiable'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 4: DÉCISION - Preuves sociales
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          
          {/* Stats bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-8 rounded-2xl mb-16" style={{ backgroundColor: 'var(--sage)' }}>
            {[
              { value: '3\'500+', label: 'Meubles montés' },
              { value: '48h', label: 'Délai d\'intervention' },
              { value: '99%', label: 'Montages réussis' },
              { value: '4.9/5', label: 'Satisfaction client' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center text-white">
                <p className="text-4xl font-bold mb-1">{stat.value}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Témoignages */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Ils nous font confiance
            </h2>
            <div className="section-divider"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { 
                name: 'Stéphanie M.', 
                city: 'Lausanne', 
                service: 'Montage IKEA',
                text: 'Armoire PAX, lit MALM, bibliothèque BILLY... tout monté en une demi-journée. Propre, rapide, professionnel. Je recommande à 100% !' 
              },
              { 
                name: 'Marco T.', 
                city: 'Genève', 
                service: 'Cuisine équipée',
                text: 'Installation complète de ma cuisine Conforama : meubles, électro, plomberie. Équipe sérieuse, résultat parfait. Prix conforme au devis.' 
              },
              { 
                name: 'Anne-Laure D.', 
                city: 'Montreux', 
                service: 'Transport + Montage',
                text: 'Achat sur Facebook Marketplace, livraison et montage le lendemain. Service flexible et pratique, exactement ce qu\'il me fallait !' 
              }
            ].map((review, idx) => (
              <div key={idx} className="card-premium p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4 italic">"{review.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[var(--sage)] flex items-center justify-center text-white font-bold">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{review.name}</p>
                    <p className="text-xs text-muted-foreground">{review.service} • {review.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Avantages rapides */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Clock, title: 'Rapidité', desc: 'Intervention sous 48h partout en Suisse romande' },
              { icon: Award, title: 'Propreté', desc: 'Chantier propre, cartons et déchets évacués' },
              { icon: Shield, title: 'Prix fixe', desc: 'Devis clair et détaillé, aucune mauvaise surprise' }
            ].map((item, i) => (
              <div key={i} className="text-center p-8 rounded-2xl" style={{ backgroundColor: 'rgba(168, 179, 156, 0.1)' }}>
                <item.icon className="h-10 w-10 mx-auto mb-4" style={{ color: 'var(--sage)' }} />
                <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--sage-dark)' }}>{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 5: ACTION - Formulaire
          ============================================ */}
      <section className="py-20" style={{ backgroundColor: 'var(--anthracite)' }} id="form">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="text-white">
              <Badge className="mb-4 bg-[var(--sage)]">Devis gratuit</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Profitez de vos meubles sans les galères
              </h2>
              <p className="text-white/70 mb-8 text-lg">
                Décrivez votre besoin et recevez un devis clair sous 24h. 
                Intervention rapide, prix fixe, résultat garanti.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  'Devis gratuit et sans engagement',
                  'Prix fixe annoncé à l\'avance',
                  'Intervention sous 48h',
                  'Évacuation des cartons incluse'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[var(--sage)] flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <ServiceLeadForm service="montage" pole="montage" questions={questions} />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section pour SEO */}
      <section className="py-16 border-t">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold mb-8 text-center">Questions fréquentes</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              { q: 'Combien coûte le montage de meubles ?', a: 'Le tarif dépend du type et de la quantité de meubles. Comptez environ 80-150 CHF pour une armoire PAX ou un lit. Nous établissons un devis gratuit sur mesure.' },
              { q: 'Montez-vous tous les types de meubles ?', a: 'Oui ! IKEA, Conforama, Pfister, Lipo, Micasa, meubles d\'occasion... Nos techniciens sont habitués à tous les systèmes de montage.' },
              { q: 'Sous quel délai intervenez-vous ?', a: 'Nous intervenons généralement sous 48h en Suisse romande. Pour les urgences, contactez-nous par téléphone.' },
              { q: 'Évacuez-vous les emballages ?', a: 'Oui, l\'évacuation des cartons et emballages est incluse dans notre prestation. Vous profitez de vos meubles sans vous soucier du reste.' }
            ].map((faq, idx) => (
              <div key={idx} className="p-6 bg-secondary/20 rounded-lg">
                <h3 className="font-semibold mb-2">{faq.q}</h3>
                <p className="text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
